import { useCallback, useEffect, useState } from 'react';

/**
 * Persisted ordering of dashboard widgets. Unknown ids are dropped and newly
 * added defaults are appended, so the layout survives code changes gracefully.
 */
export const useLayoutOrder = (storageKey: string, defaultIds: string[]) => {
  const [order, setOrder] = useState<string[]>(defaultIds);
  const defaultsKey = defaultIds.join('|');

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        setOrder(defaultIds);
        return;
      }
      const saved = JSON.parse(raw) as string[];
      const known = saved.filter((id) => defaultIds.includes(id));
      const missing = defaultIds.filter((id) => !known.includes(id));
      setOrder([...known, ...missing]);
    } catch {
      setOrder(defaultIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, defaultsKey]);

  const save = useCallback(
    (next: string[]) => {
      setOrder(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [storageKey],
  );

  return { order, save };
};
